import React, { useState, useEffect, useMemo } from "react";
import API from "@/lib/api";
import { useNavigate } from "react-router-dom";
import SearchableFilter from "@/components/SearchableFilter";
import useTableConfig from "@/hooks/useTableConfig";
import { ChevronLeft, ChevronRight, Search, ArrowUpDown } from "lucide-react";
import { Button } from "@/components/ui/button";

const BAR_COLORS = ["#5B9BD5","#9B72CF","#E6A23C","#67B168","#E06666","#C2956B","#7B7BC9","#C97BA3"];
const PRIORITY_ORDER = { p0: 0, p1: 1, p2: 2, p3: 3, p4: 4 };
const PRIORITY_LABELS = { p0: "P0", p1: "P1", p2: "P2", p3: "P3", p4: "P4" };
const STATUS_ITEMS = [{ value: "active", label: "Active" }, { value: "in_progress", label: "In Progress" }, { value: "completed", label: "Completed" }];
function colorIdx(s) { let h=0; for(let i=0;i<(s||"").length;i++) h=s.charCodeAt(i)+((h<<5)-h); return Math.abs(h); }
function localDateStr(d) { return [d.getFullYear(), String(d.getMonth()+1).padStart(2,'0'), String(d.getDate()).padStart(2,'0')].join('-'); }
function addMonths(ds, n) { const d = new Date(ds + "T12:00:00"); d.setMonth(d.getMonth() + n); return localDateStr(d); }
function diffDays(a, b) { return Math.round((new Date(b+"T12:00:00") - new Date(a+"T12:00:00")) / 86400000); }
function formatMonth(ds) { return new Date(ds + "T12:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" }); }

const LABEL_W = 220;

function pctToDate(pctVal, viewStart, totalDays) {
  const days = Math.round((pctVal / 100) * totalDays);
  const d = new Date(viewStart + "T12:00:00");
  d.setDate(d.getDate() + days);
  return localDateStr(d);
}

function BarWithDrag({ barLeft, barWidth, barColor, proj, s, e, progress, viewStart, totalDays, navigate, onUpdate }) {
  const [drag, setDrag] = useState(null); // { type: 'start'|'end'|'move', startX, origLeft, origWidth }
  const [localLeft, setLocalLeft] = useState(barLeft);
  const [localWidth, setLocalWidth] = useState(barWidth);
  const containerRef = React.useRef(null);

  // Sync with props when not dragging
  React.useEffect(() => { if (!drag) { setLocalLeft(barLeft); setLocalWidth(barWidth); } }, [barLeft, barWidth, drag]);

  const startDrag = (type, ev) => {
    ev.preventDefault(); ev.stopPropagation();
    const chartArea = containerRef.current?.closest('.flex-1.relative');
    if (!chartArea) return;
    const rect = chartArea.getBoundingClientRect();
    setDrag({ type, startX: ev.clientX, origLeft: localLeft, origWidth: localWidth, chartW: rect.width });

    const overlay = document.createElement('div');
    overlay.style.cssText = 'position:fixed;inset:0;z-index:9999;cursor:' + (type === 'move' ? 'grabbing' : 'col-resize') + ';';
    document.body.appendChild(overlay);

    const onMove = (me) => {
      const dx = ((me.clientX - ev.clientX) / rect.width) * 100;
      if (type === 'start') {
        const newL = Math.max(0, Math.min(localLeft + localWidth - 1, localLeft + dx));
        const newW = localWidth - (newL - localLeft);
        setLocalLeft(newL); setLocalWidth(newW);
      } else if (type === 'end') {
        setLocalWidth(Math.max(1, localWidth + dx));
      } else {
        setLocalLeft(Math.max(0, localLeft + dx));
      }
    };
    const onUp = (ue) => {
      overlay.remove();
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      // Compute final dates
      const dx = ((ue.clientX - ev.clientX) / rect.width) * 100;
      let fL = localLeft, fW = localWidth;
      if (type === 'start') { fL = Math.max(0, localLeft + dx); fW = localWidth - dx; }
      else if (type === 'end') { fW = Math.max(1, localWidth + dx); }
      else { fL = Math.max(0, localLeft + dx); }
      const newStart = pctToDate(fL, viewStart, totalDays);
      const newEnd = pctToDate(fL + fW, viewStart, totalDays);
      setDrag(null);
      if (newStart !== s || newEnd !== e) onUpdate(newStart, newEnd);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const fmtDate = (ds) => new Date(ds + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" });
  const curStart = drag ? pctToDate(localLeft, viewStart, totalDays) : s;
  const curEnd = drag ? pctToDate(localLeft + localWidth, viewStart, totalDays) : e;

  return (
    <div ref={containerRef} className="group/bar absolute" style={{ left: localLeft + "%", width: localWidth + "%", top: 0, bottom: 0 }}>
      {/* Date markers above */}
      <div className={"absolute left-0 top-[1px] pointer-events-none z-20 " + (drag ? "opacity-100" : "opacity-0 group-hover/bar:opacity-100") + " transition-opacity"}>
        <div className="text-[9px] font-medium px-1.5 py-0.5 rounded-[3px] whitespace-nowrap shadow-sm" style={{ backgroundColor: barColor, color: "#fff" }}>{fmtDate(curStart)}</div>
      </div>
      <div className={"absolute right-0 top-[1px] pointer-events-none z-20 " + (drag ? "opacity-100" : "opacity-0 group-hover/bar:opacity-100") + " transition-opacity"}>
        <div className="text-[9px] font-medium px-1.5 py-0.5 rounded-[3px] whitespace-nowrap shadow-sm" style={{ backgroundColor: barColor, color: "#fff" }}>{fmtDate(curEnd)}</div>
      </div>
      {/* Bar body */}
      <div className="absolute left-0 right-0 top-[18px] h-[28px] rounded-[3px] flex items-center overflow-hidden cursor-grab active:cursor-grabbing"
        style={{ backgroundColor: barColor + "25", border: `1px solid ${barColor}40` }}
        onMouseDown={ev => startDrag('move', ev)}
        onClick={ev => { if (!drag) navigate("/projects/" + proj.project_id); }}>
        <div className="h-full rounded-l-[2px]" style={{ width: Math.min(progress, 100) + "%", backgroundColor: barColor + "90" }} />
        <span className="absolute inset-0 flex items-center px-2 text-[10px] font-semibold truncate" style={{ color: barColor }}>{proj.title}</span>
      </div>
      {/* Left drag handle (start date) */}
      <div className="absolute left-[-3px] top-[16px] w-[6px] h-[32px] cursor-col-resize z-10 opacity-0 group-hover/bar:opacity-100 hover:opacity-100 transition-opacity"
        onMouseDown={ev => startDrag('start', ev)}>
        <div className="w-[3px] h-full rounded-full mx-auto" style={{ backgroundColor: barColor }} />
      </div>
      {/* Right drag handle (end date) */}
      <div className="absolute right-[-3px] top-[16px] w-[6px] h-[32px] cursor-col-resize z-10 opacity-0 group-hover/bar:opacity-100 hover:opacity-100 transition-opacity"
        onMouseDown={ev => startDrag('end', ev)}>
        <div className="w-[3px] h-full rounded-full mx-auto" style={{ backgroundColor: barColor }} />
      </div>
      {/* Tooltip */}
      <div className={"absolute left-0 top-[48px] pointer-events-none z-20 " + (drag ? "opacity-100" : "opacity-0 group-hover/bar:opacity-100") + " transition-opacity"}>
        <div className="bg-[#37352F] text-white text-[10px] px-2 py-1 rounded-[3px] whitespace-nowrap shadow-lg">
          {fmtDate(curStart)} — {fmtDate(curEnd)} · {diffDays(curStart, curEnd)} days{progress > 0 ? ` · ${progress}% done` : ""}
        </div>
      </div>
    </div>
  );
}

export default function Timeline() {
  const [projects, setProjects] = useState([]);
  const [people, setPeople] = useState([]);
  const [search, setSearch] = useState("");
  const { getFilter, setFilter: pf } = useTableConfig("timeline", []);
  const [assignedFilter, setAssignedFilterL] = useState(() => getFilter("assigned"));
  const [goalFilter, setGoalFilterL] = useState(() => getFilter("goal"));
  const [statusFilter, setStatusFilterL] = useState(() => getFilter("status"));
  const [hideCompleted, setHideCompletedL] = useState(() => { const v = getFilter("hide"); return v.length ? v[0] === "true" : true; });
  const setAssignedFilter = (v) => { setAssignedFilterL(v); pf("assigned", v); };
  const setGoalFilter = (v) => { setGoalFilterL(v); pf("goal", v); };
  const setStatusFilter = (v) => { setStatusFilterL(v); pf("status", v); };
  const setHideCompleted = (v) => { setHideCompletedL(v); pf("hide", [String(v)]); };
  const [sortByPriority, setSortByPriority] = useState(true);
  const navigate = useNavigate();
  const [startMonth, setStartMonth] = useState(() => { const d = new Date(); return localDateStr(new Date(d.getFullYear(), d.getMonth(), 1)); });

  useEffect(() => {
    API.get("/projects/all").then(r => setProjects(r.data)).catch(() => {});
    API.get("/people").then(r => setPeople(r.data)).catch(() => {});
  }, []);

  const viewStart = startMonth;
  const viewEnd = addMonths(startMonth, 3);
  const totalDays = diffDays(viewStart, viewEnd);
  const pct = (ds) => totalDays > 0 ? (diffDays(viewStart, ds) / totalDays) * 100 : 0;

  const prevPeriod = () => setStartMonth(addMonths(startMonth, -1));
  const nextPeriod = () => setStartMonth(addMonths(startMonth, 1));
  const goNow = () => { const d = new Date(); setStartMonth(localDateStr(new Date(d.getFullYear(), d.getMonth(), 1))); };

  const goalItems = [...new Map(projects.filter(p => p.goal_title).map(p => [p.goal_id, { value: p.goal_id, label: p.goal_title }])).values()];
  const assignedItems = useMemo(() => {
    const m = new Map();
    m.set("__unassigned__", { value: "__unassigned__", label: "Unassigned" });
    projects.forEach(p => (p.people_involved || []).forEach(pid => {
      const person = people.find(x => x.person_id === pid);
      if (person) m.set(pid, { value: pid, label: person.name });
    }));
    return Array.from(m.values());
  }, [projects, people]);

  let filtered = projects.filter(p => {
    if (hideCompleted && p.status === "completed") return false;
    if (statusFilter.length > 0 && !statusFilter.includes(p.status || "active")) return false;
    if (goalFilter.length > 0 && !goalFilter.includes(p.goal_id)) return false;
    if (search && !p.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (assignedFilter.length > 0) {
      const ppl = p.people_involved || [];
      if (assignedFilter.includes("__unassigned__") && ppl.length === 0) {}
      else if (assignedFilter.some(f => f !== "__unassigned__" && ppl.includes(f))) {}
      else return false;
    }
    if (!p.start_date && !p.due_date) return false;
    return true;
  });
  if (sortByPriority) filtered = [...filtered].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 5) - (PRIORITY_ORDER[b.priority] ?? 5));

  const monthLines = [];
  let cur = new Date(viewStart + "T12:00:00");
  const endD = new Date(viewEnd + "T12:00:00");
  while (cur < endD) {
    const ms = localDateStr(cur);
    monthLines.push({ date: ms, pct: pct(ms), label: formatMonth(ms) });
    cur.setMonth(cur.getMonth() + 1);
  }

  const today = localDateStr(new Date());
  const todayPct = pct(today);

  return (
    <div data-testid="timeline-page">
      <h1 className="text-[28px] font-bold text-[#37352F] mb-1">Timeline</h1>
      <p className="text-[13px] text-[#6B6B6B] mb-3">Gantt view of projects with start and due dates</p>

      {/* Time navigation */}
      <div className="flex items-center gap-2 mb-3">
        <Button onClick={prevPeriod} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-[14px] font-medium text-[#37352F] min-w-[180px] text-center">{formatMonth(viewStart)} — {formatMonth(addMonths(viewStart, 2))}</span>
        <Button onClick={nextPeriod} variant="ghost" size="icon" className="text-[#6B6B6B] h-7 w-7"><ChevronRight className="w-4 h-4" /></Button>
        <button onClick={goNow} className="text-[12px] text-[#2383E2] hover:underline ml-1">Today</button>
      </div>

      {/* Filters - matching AllProjects */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <div className="relative flex-1 max-w-[220px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#A3A3A0]" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search projects..."
            className="w-full pl-8 pr-3 py-[5px] text-[13px] border border-[#E0E0DE] rounded-[4px] bg-transparent outline-none focus:border-[#2383E2]/50 text-[#37352F] placeholder:text-[#A3A3A0]" />
        </div>
        <button onClick={() => setHideCompleted(!hideCompleted)} className={"text-[12px] px-2 py-1 border rounded-[4px] transition-colors " + (hideCompleted ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B]")}>{hideCompleted ? "Show completed" : "Hide completed"}</button>
        <SearchableFilter label="Status" items={STATUS_ITEMS} values={statusFilter} onChange={setStatusFilter} />
        <SearchableFilter label="Goal" items={goalItems} values={goalFilter} onChange={setGoalFilter} />
        <SearchableFilter label="Assigned" items={assignedItems} values={assignedFilter} onChange={setAssignedFilter} />
        <button onClick={() => setSortByPriority(!sortByPriority)} className={"flex items-center gap-1 px-2 py-[5px] text-[12px] border rounded-[4px] transition-colors " + (sortByPriority ? "border-[#2383E2] text-[#2383E2] bg-[#2383E2]/5" : "border-[#E0E0DE] text-[#6B6B6B] hover:bg-[#E8E8E6]")}><ArrowUpDown className="w-3 h-3" /> Priority</button>
      </div>

      {/* Timeline chart */}
      <div className="border border-[#E0E0DE] rounded-[4px] overflow-x-auto">
        <div className="flex bg-[#F7F6F3] border-b border-[#E0E0DE]" style={{ minWidth: LABEL_W + 800 }}>
          <div className="shrink-0 border-r border-[#E0E0DE] text-[11px] font-medium text-[#6B6B6B] py-2 px-3" style={{ width: LABEL_W }}>Project</div>
          <div className="flex-1 relative flex">
            {monthLines.map((m, i) => {
              const nextPct = i < monthLines.length - 1 ? monthLines[i+1].pct : 100;
              return (<div key={m.date} className="text-[11px] font-medium text-[#6B6B6B] py-2 text-center border-r border-[#E0E0DE] last:border-r-0" style={{ width: (nextPct - m.pct) + "%" }}>{m.label}</div>);
            })}
          </div>
        </div>

        <div style={{ minWidth: LABEL_W + 800 }}>
          {filtered.length === 0 ? (
            <div className="text-center py-12 text-[#6B6B6B] text-[14px]">No projects with dates to display.</div>
          ) : filtered.map(proj => {
            const s = proj.start_date || proj.due_date;
            const e = proj.due_date || proj.start_date;
            const barLeft = Math.max(0, Math.min(100, pct(s)));
            const barWidth = Math.max(0.5, Math.min(100, pct(e)) - barLeft);
            const ci = colorIdx(proj.title);
            const barColor = BAR_COLORS[ci % BAR_COLORS.length];
            const progress = proj.task_count > 0 ? Math.round((proj.done_count / proj.task_count) * 100) : 0;
            const priorityLabel = PRIORITY_LABELS[proj.priority];

            return (
              <div key={proj.project_id} className="flex border-b border-[#E8E8E6] hover:bg-[#F7F6F3]/30" style={{ minHeight: 68 }}>
                <div className="shrink-0 px-3 py-2 border-r border-[#E0E0DE] bg-white flex flex-col justify-center" style={{ width: LABEL_W }}>
                  <div className="flex items-center gap-1.5">
                    {priorityLabel && <span className="text-[9px] font-bold px-1 py-0.5 rounded-[2px] bg-[#E8E8E8] text-[#555] shrink-0">{priorityLabel}</span>}
                    <button onClick={() => navigate("/projects/" + proj.project_id)} className="text-[13px] font-medium text-[#37352F] hover:text-[#2383E2] hover:underline truncate text-left">{proj.title}</button>
                  </div>
                  <div className="text-[11px] text-[#6B6B6B] truncate mt-0.5">
                    {proj.goal_title || ""}
                    {proj.status === "in_progress" && <span className="ml-1 text-[#2383E2]">In Progress</span>}
                    {progress > 0 && <span className="ml-1">{progress}%</span>}
                  </div>
                  {(proj.people_involved_names || []).length > 0 && (
                    <div className="flex flex-wrap gap-0.5 mt-1">
                      {proj.people_involved_names.map((name, i) => (
                        <span key={i} className="text-[9px] px-1.5 py-0.5 bg-[#E8E8E6] rounded-[3px] text-[#37352F]">{name}</span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex-1 relative">
                  {monthLines.map(m => <div key={m.date} className="absolute top-0 bottom-0 border-l border-[#E8E8E6]" style={{ left: m.pct + "%" }} />)}
                  {todayPct >= 0 && todayPct <= 100 && <div className="absolute top-0 bottom-0 w-[2px] bg-[#EB5757] z-10" style={{ left: todayPct + "%" }} />}
                  {barLeft < 100 && (barLeft + barWidth) > 0 && (
                    <BarWithDrag barLeft={barLeft} barWidth={barWidth} barColor={barColor} proj={proj} s={s} e={e} progress={progress}
                      viewStart={viewStart} totalDays={totalDays} navigate={navigate}
                      onUpdate={(newStart, newEnd) => {
                        API.patch("/projects/" + proj.project_id, { start_date: newStart, due_date: newEnd }).then(() => {
                          API.get("/projects/all").then(r => setProjects(r.data));
                        });
                      }} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-[11px] text-[#A3A3A0] mt-2">Red line = today. Only projects with dates are shown.</p>
    </div>
  );
}
